import {
  ChevronDownIcon,
  FileSearch01Icon,
  Maximize02Icon,
  PlayCircleIcon,
  PlusIcon,
  XIcon,
} from '@langchain/untitled-ui-icons';

import { useContext, useMemo, useState } from 'react';
import {
  Link,
  useLocation,
  useParams,
  useSearchParams,
} from 'react-router-dom';

import { Popover, PopoverContent, PopoverTrigger } from '@/components/Popover';
import { Button, IconButton, Tooltip } from '@/design-system';
import { OrgConfigs, useOrgConfig } from '@/hooks/useOrgConfig';
import { usePermissions } from '@/hooks/usePermissions.tsx';
import { useOrganizationId } from '@/hooks/useSwr';
import { mutateWithInfinite } from '@/hooks/useSwr.utils';
import MessageChatIcon from '@/icons/MessageChatIcon.svg?react';
import {
  apiDatasetsPath,
  apiPublicPath,
  appAnnotationQueuesPath,
  appGraphIndexPath,
  appOrganizationPath,
  appProjectsPath,
  appThreadPath,
  deploymentsAppIndexPath,
} from '@/utils/constants';

import { AddToAnnotationQueueButton } from '../../components/AnnotationQueuePopover/AddToAnnotationQueueButton';
import { ExampleCrudPaneWithButton } from '../../components/ExampleCrudPane/ExampleCrudPaneWithButton';
import { RunFeedbackButtons } from '../../components/RunFeedbackButtons';
import { ShareAction } from '../../components/RunsTable/components/ShareAction';
import { Skeleton } from '../../components/Skeleton';
import { RunSchema } from '../../types/schema';
import { apiRunsPath } from '../../utils/constants';
import {
  CloneThreadButtonWithForm,
  GraphConnectPopover,
} from '../Graph/GraphConnectPopover';
import { getGraphUrlFromRun } from '../Graph/GraphThreadPage.utils';
import { StateContext } from '../Graph/src/control/state';
import { usePlaygroundAvailableForRun } from '../Playground/utils/Playground.utils';

export const RunHeaderThreadButton = (props: {
  run: RunSchema;
  size?: 'sm' | 'md';
}) => {
  const tenantId = useOrganizationId();
  const { conversationId: urlConversationId } = useParams();
  const [searchParams] = useSearchParams();
  const peekedConversationId = searchParams.get('peekedConversationId');
  const { pathname } = useLocation();
  const inStudio = pathname.includes(appGraphIndexPath);

  const { metadata: runMetadata } = props.run.extra ?? {};
  const conversationId =
    runMetadata?.thread_id ??
    runMetadata?.session_id ??
    runMetadata?.conversation_id;

  if (
    !conversationId ||
    conversationId === urlConversationId ||
    conversationId === peekedConversationId ||
    inStudio
  )
    return null;
  return (
    <Link
      className="inline-flex"
      to={`/${appOrganizationPath}/${tenantId}/${appProjectsPath}/p/${props.run.session_id}/${appThreadPath}/${conversationId}`}
    >
      {props.size === 'sm' ? (
        <IconButton
          size="sm"
          color="secondary"
          variant="outlined"
          icon={MessageChatIcon}
          label="Thread"
          tooltipProps={{
            side: 'bottom',
          }}
        />
      ) : (
        <Button size="sm" variant="outlined" color="secondary">
          Thread
        </Button>
      )}
    </Link>
  );
};

export const LgpLogsButton = (props: {
  run: RunSchema;
  size?: 'sm' | 'md';
}) => {
  const tenantId = useOrganizationId();
  const { value: langGraphRemoteReconcilerEnabled } = useOrgConfig(
    OrgConfigs.langgraph_remote_reconciler_enabled
  );
  const { metadata: runMetadata } = props.run.extra ?? {};

  const deploymentId = runMetadata?.LANGSMITH_HOST_PROJECT_ID;
  const revisionId = runMetadata?.LANGSMITH_HOST_REVISION_ID;

  if (!deploymentId || !revisionId || langGraphRemoteReconcilerEnabled)
    return null;

  return (
    <Link
      to={`/${appOrganizationPath}/${tenantId}/${deploymentsAppIndexPath}/${deploymentId}?revisionPeek=${revisionId}&peekTraceIdForLogs=${props.run.id}`}
      className="inline-flex"
    >
      {props.size === 'sm' ? (
        <IconButton
          size="sm"
          color="secondary"
          variant="outlined"
          icon={FileSearch01Icon}
          label="See Logs"
          tooltipProps={{
            side: 'bottom',
          }}
        />
      ) : (
        <Button type="button" size="sm" variant="outlined" color="secondary">
          See Logs
        </Button>
      )}
    </Link>
  );
};

const PlaygroundOrStudioButton = ({
  run,
  onNavigate,
  priorThreadRunIds,
  size = 'md',
  actionCollapsedSize = 'full',
}: {
  run: RunSchema;
  priorThreadRunIds?: string[];
  onNavigate: (run: RunSchema) => void;
  size?: 'sm' | 'md';
  actionCollapsedSize?: 'full' | 'half' | 'closed';
}) => {
  const { pathname } = useLocation();
  const inStudio = pathname.includes(appGraphIndexPath);
  const organizationId = useOrganizationId();
  const { isAvailable: isPlaygroundAvailableForRun, isLoading } =
    usePlaygroundAvailableForRun(run);
  const graphUrl = getGraphUrlFromRun(organizationId, run);

  const { metadata: runMetadata } = run.extra ?? {};
  const threadId = runMetadata?.['thread_id'] ?? runMetadata?.['THREAD_ID'];

  const { setSelectedPlaygroundRun } = useContext(StateContext);

  const state = isLoading
    ? 'loading'
    : isPlaygroundAvailableForRun
    ? 'manifest'
    : run?.run_type === 'llm'
    ? 'fallback'
    : graphUrl != null || !!threadId
    ? 'graph'
    : priorThreadRunIds !== undefined
    ? 'thread'
    : 'none';

  const title = useMemo(() => {
    if (state === 'none') {
      return 'The LangSmith playground is currently only available for LLMs and Chat Models.';
    } else if (state === 'fallback') {
      return 'Unable to detect the LLM provider, defaulting to OpenAI';
    } else if (state === 'thread') {
      return 'Open thread messages up to this turn in playground';
    }
    return '';
  }, [state]);

  const button = () => {
    if (state === 'loading') {
      return size === 'sm' ? (
        <Skeleton className="h-8 w-8" />
      ) : (
        <Skeleton className="h-8 w-32" />
      );
    }
    if (state === 'graph') {
      if (inStudio) {
        return <CloneThreadButtonWithForm rootRun={run} size={size} />;
      }
      return (
        <GraphConnectPopover
          size={'sm'}
          buttonWidth={actionCollapsedSize === 'closed' ? 'sm' : 'md'}
          rootRun={run}
          buttonLabel="Run in Studio"
          connectionTypes={['local', 'deployed']}
          graphUrl={graphUrl}
        />
      );
    }
    if (size === 'sm') {
      return (
        <IconButton
          data-testid="playground-button"
          size="sm"
          variant="outlined"
          color="primary"
          disabled={state === 'none'}
          onClick={() => {
            if (inStudio) {
              setSelectedPlaygroundRun(run);
            } else {
              onNavigate(run);
            }
          }}
          icon={PlayCircleIcon}
          label="Playground"
          tooltipProps={{
            side: 'bottom',
          }}
        />
      );
    }
    return (
      <Button
        data-testid="playground-button"
        size="sm"
        variant="outlined"
        color="primary"
        leftDecorator={PlayCircleIcon}
        disabled={state === 'none'}
        onClick={() => {
          if (inStudio) {
            setSelectedPlaygroundRun(run);
          } else {
            onNavigate(run);
          }
        }}
      >
        Playground
      </Button>
    );
  };

  return (
    <Tooltip title={title} side="bottom" align="start">
      {button()}
    </Tooltip>
  );
};

export function RunActions({
  run,
  isPublicPage,
  priorThreadRunIds,
  onPlaygroundNavigate,
  isComparing,
  onClose,
  actionCollapsedSize,
}: {
  run: RunSchema;
  isPublicPage: boolean;
  priorThreadRunIds?: string[];
  onPlaygroundNavigate: (run: RunSchema) => void;
  isComparing?: boolean;
  onClose?: () => void;
  actionCollapsedSize?: 'full' | 'half' | 'closed';
}) {
  const { authorize } = usePermissions();

  const { pathname } = useLocation();
  const inStudio = pathname.includes(appGraphIndexPath);
  const isAnnotationQueue = pathname.includes(appAnnotationQueuesPath);

  const [addToOpen, setAddToOpen] = useState(false);

  const addTo = (authorize('datasets:update') ||
    authorize('annotation-queues:update')) && (
    <Popover open={addToOpen} onOpenChange={setAddToOpen}>
      <PopoverTrigger asChild>
        {actionCollapsedSize !== 'full' ? (
          <IconButton
            size="sm"
            color="secondary"
            variant="outlined"
            icon={PlusIcon}
            label="Add to"
            tooltipProps={{
              side: 'bottom',
            }}
          />
        ) : (
          <Button
            size="sm"
            color="secondary"
            variant="outlined"
            rightDecorator={ChevronDownIcon}
          >
            Add to
          </Button>
        )}
      </PopoverTrigger>
      <ExampleCrudPaneWithButton
        key={run.id}
        run={run}
        disabled={run.status === 'pending'}
        onSuccess={() => {
          mutateWithInfinite((key) => key?.url.startsWith(apiRunsPath));
        }}
        trigger={(onExpandDataset) => (
          <PopoverContent className="flex w-[200px] flex-col p-1">
            {authorize('datasets:update') && (
              <Button
                type="button"
                color="secondary"
                variant="plain"
                onClick={() => {
                  setAddToOpen(false);
                  onExpandDataset();
                }}
                className="w-full justify-start"
              >
                Add to Dataset
              </Button>
            )}
            {authorize('annotation-queues:update') && (
              <AddToAnnotationQueueButton
                variant="actions"
                runIds={[run.id]}
                onSuccess={() => {
                  mutateWithInfinite((key) => key?.url.startsWith(apiRunsPath));
                  setAddToOpen(false);
                }}
                trigger={(onExpandAnnotationQueue) => (
                  <Button
                    type="button"
                    color="secondary"
                    variant="plain"
                    onClick={() => {
                      onExpandAnnotationQueue();
                    }}
                    className="w-full justify-start"
                  >
                    Add to Annotation Queue
                  </Button>
                )}
              />
            )}
          </PopoverContent>
        )}
      />
    </Popover>
  );

  const playgroundButton = (
    <PlaygroundOrStudioButton
      run={run}
      onNavigate={onPlaygroundNavigate}
      priorThreadRunIds={priorThreadRunIds}
      size={actionCollapsedSize !== 'full' ? 'sm' : 'md'}
      actionCollapsedSize={actionCollapsedSize}
    />
  );
  const shareButton = (variant: 'icon' | 'button') => {
    if (!authorize('runs:share')) return null;
    return <ShareAction run={run} variant={variant} />;
  };

  const renderActions = () => {
    if (isComparing) return null;
    if (isPublicPage) {
      return playgroundButton;
    }
    if (isAnnotationQueue) {
      return (
        <>
          {playgroundButton}
          {shareButton('button')}
        </>
      );
    }
    return (
      <>
        {playgroundButton}
        {run.trace_id === run.id && (
          <>
            <LgpLogsButton
              run={run}
              size={actionCollapsedSize !== 'full' ? 'sm' : 'md'}
            />
            <RunHeaderThreadButton
              run={run}
              size={actionCollapsedSize !== 'full' ? 'sm' : 'md'}
            />
          </>
        )}
        {addTo}
        {shareButton('icon')}
        {authorize('feedback:create') && (
          <RunFeedbackButtons
            run={run}
            disabled={run.status === 'pending'}
            onFeedbackGiven={() => {
              // check implementation of useExampleRunsInfinite to
              // see the correct key to mutate.
              mutateWithInfinite((key) => {
                if (isPublicPage) {
                  return (
                    key?.url.startsWith(apiPublicPath) &&
                    key?.url.endsWith('/examples/runs')
                  );
                } else {
                  return (
                    key?.url.startsWith(apiDatasetsPath) &&
                    key?.url.endsWith('/runs')
                  );
                }
              });
            }}
            condensed={true}
          />
        )}
        {inStudio && (
          <Link to={run.app_path}>
            <IconButton
              size="sm"
              color="secondary"
              variant="outlined"
              icon={Maximize02Icon}
              label="Open in LangSmith"
            />
          </Link>
        )}
        {onClose && (
          <IconButton
            size="sm"
            color="secondary"
            variant="outlined"
            onClick={onClose}
            icon={XIcon}
            label="Close"
            tooltipProps={{
              side: 'bottom',
            }}
          />
        )}
      </>
    );
  };

  return <div className="flex items-center gap-1.5">{renderActions()}</div>;
}
